import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ListPlus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import { PUBLIC_BASE } from '../../pages/public/config';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function AlbumCard({ album }) {
  const navigate = useNavigate();
  const { addBulkToPlaylist, playImmediate } = useMusicPlayer();

  // null=미로드, 'loading'=로딩중, []=곡없음, [...]= 재생가능
  const [preloaded, setPreloaded] = useState(null);
  const loadingRef = useRef(false);

  const buildTrack = (song) => ({
    id: song.id,
    nameKr: song.nameKr,
    artistKr: song.artistKr || album.artist?.nameKr || '',
    fileUrl: song.fileUrl,
    albumImage: album.image || FALLBACK_IMG,
    albumName: album.nameKr,
  });

  const fetchTracks = async () => {
    const res = await axios.get(`/public/albums/${album.id}`);
    return (res.data.songs || []).filter((s) => s.fileUrl).map(buildTrack);
  };

  // 마우스 진입 시 곡 목록 미리 로드
  const handleMouseEnter = () => {
    if (preloaded !== null || loadingRef.current) return;
    loadingRef.current = true;
    setPreloaded('loading');
    fetchTracks()
      .then((tracks) => setPreloaded(tracks))
      .catch(() => setPreloaded([]))
      .finally(() => { loadingRef.current = false; });
  };

  const handlePlay = async (e) => {
    e.stopPropagation();
    if (preloaded === 'loading') return;
    if (Array.isArray(preloaded)) {
      if (preloaded.length > 0) playImmediate(preloaded);
      return;
    }
    // hover 없이 바로 클릭한 경우
    try {
      const tracks = await fetchTracks();
      setPreloaded(tracks);
      if (tracks.length > 0) playImmediate(tracks);
    } catch (err) {
      console.error('AlbumCard 재생 실패:', err);
    }
  };

  const handleAddToPlaylist = async (e) => {
    e.stopPropagation();
    if (preloaded === 'loading') return;
    if (Array.isArray(preloaded)) {
      addBulkToPlaylist(preloaded);
      return;
    }
    try {
      const tracks = await fetchTracks();
      setPreloaded(tracks);
      addBulkToPlaylist(tracks);
    } catch (err) {
      console.error('AlbumCard 재생목록 추가 실패:', err);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('T')[0].split('-');
    return `${y}.${m}.${day}`;
  };

  const isLoading = preloaded === 'loading';
  const noAudio = Array.isArray(preloaded) && preloaded.length === 0;

  return (
    <div
      className="group bg-[#182334] rounded-xl overflow-hidden hover:bg-[#1e2e46] transition-all duration-200 card-shimmer card-lift"
      onMouseEnter={handleMouseEnter}>
      {/* 앨범 이미지 */}
      <div className="relative aspect-square">
        <img
          src={album.image || FALLBACK_IMG}
          alt={album.nameKr}
          className="w-full h-full object-cover"
        />

        {/* hover 오버레이: 로딩 중엔 스피너만, 완료 후 버튼 표시 */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          {isLoading ? (
            <Loader2 size={22} className="text-white animate-spin" />
          ) : (
            <>
              <button
                onClick={handlePlay}
                disabled={noAudio}
                className={`w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                  noAudio ? 'bg-slate-700 cursor-not-allowed' : 'bg-sky-500 hover:bg-sky-400'
                }`}>
                <Play size={18} fill="currentColor" className={`ml-0.5 ${noAudio ? 'text-slate-500' : 'text-white'}`} />
              </button>
              <button
                onClick={handleAddToPlaylist}
                disabled={noAudio}
                title="담기"
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                  noAudio ? 'bg-slate-800 cursor-not-allowed' : 'bg-white/20 hover:bg-white/30'
                }`}>
                <ListPlus size={16} className={noAudio ? 'text-slate-600' : 'text-white'} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* 앨범 정보 */}
      <div className="p-3">
        <p
          onClick={() => navigate(`${PUBLIC_BASE}/albums/${album.id}`)}
          className="text-white text-sm font-medium truncate cursor-pointer hover:text-sky-400 transition-colors">
          {album.nameKr}
        </p>
        <p
          onClick={() => album.artist?.id && navigate(`${PUBLIC_BASE}/artists/${album.artist.id}`)}
          className={`text-xs truncate mt-0.5 transition-colors ${album.artist?.id ? 'text-slate-400 cursor-pointer hover:text-sky-400' : 'text-slate-400'}`}>
          {album.artist?.nameKr || ''}
        </p>
        {album.releaseDateTime && (
          <p className="text-slate-600 text-xs mt-0.5">
            {formatDate(album.releaseDateTime)}
          </p>
        )}
      </div>
    </div>
  );
}

export default AlbumCard;
